import React, { useLayoutEffect, useRef, useState } from "react";
import type { TableColumnsType } from "antd";
import styled from "styled-components";
import { COLORS, LIGHT_THEME_COLORS } from "styles/colors";
import InfiniteWrapper from "components/blocks/table/InfiniteWrapper";
import { CommonTable, Wrapper } from "./AntdTable";
import { ExpandableConfig } from "antd/es/table/interface";
import NoDataChart from "components/templates/nopage/NoDataChart";
import Spinner from "../loader/Spinner";

export const DashboardChartDetailTable = ({
  data,
  loadMore,
  isLoading,
  hasMore,
  columns,
  expandable,
  rowKey,
}: {
  data: any[];
  loadMore: () => void;
  isLoading: boolean;
  hasMore: boolean;
  columns: TableColumnsType<any>;
  expandable?: ExpandableConfig<any>;
  rowKey?: string | ((record: any) => React.Key);
}): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [height, setHeight] = useState<number>(500);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setHeight(el.clientHeight);

    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect?.height ?? 0;
      setHeight(h);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [isLoading]);
  // 스티키 헤더 테이블의 colgroup이 비어 있을 때, 바디 테이블의 colgroup으로 덮어씌움
  useLayoutEffect(() => {
    const syncColgroup = () => {
      const container = containerRef.current;
      if (!container) return;
      const headerColgroup = container.querySelector(
        ".ant-table-sticky-holder table colgroup"
      );
      const bodyColgroup = container.querySelector(
        ".ant-table-body table colgroup"
      );
      if (!headerColgroup || !bodyColgroup) return;
      const bodyCols = bodyColgroup.querySelectorAll("col");
      if (bodyCols.length === 0) return;
      headerColgroup.innerHTML = bodyColgroup.innerHTML;
    };

    const rafId = requestAnimationFrame(() => {
      syncColgroup();
      requestAnimationFrame(syncColgroup);
    });
    const timeoutId = setTimeout(syncColgroup, 150);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [data, columns]);

  return (
    <DashboardChartTableWrapper ref={containerRef}>
      <InfiniteWrapper
        data={data}
        loadMore={loadMore}
        isLoading={isLoading}
        hasMore={hasMore}
        height={`${height}px`}
      >
        <CommonTable
          rowKey={rowKey}
          columns={columns}
          dataSource={data}
          locale={{
            emptyText:
              data.length === 0 && !isLoading && !hasMore ? (
                <NoDataChart />
              ) : (
                <Spinner />
              ),
          }}
          sticky={true}
          pagination={false}
          loading={false}
          expandable={expandable}
        />
      </InfiniteWrapper>
    </DashboardChartTableWrapper>
  );
};

const DashboardChartTableWrapper = styled(Wrapper)<{ minHeight?: number }>`
  display: flex;
  flex-direction: column;

  .table-wrapper {
    flex: 1 1 auto;
    .ant-table-expand-icon-col {
      width: 25px !important;
    }
  }

  .pagination-wrapper {
    flex: 0 0 auto;
  }
  .ant-table-tbody > .ant-table-expanded-row .ant-table-content {
    min-height: unset !important;
    .ant-table-cell {
      border-top: none !important;
    }
  }
  .ant-table-tbody
    > .ant-table-expanded-row
    > .ant-table-cell:last-child:first-child {
    padding: 0 !important;
  }

  .ant-table-thead th {
    padding: 4px 16px !important;
    background-color: ${({ theme }) =>
      theme.value === "light"
        ? LIGHT_THEME_COLORS.LIGHTGRAY
        : COLORS.PRIMARY3} !important;

    .ant-table-column-title {
      font-weight: 500 !important;
    }
  }
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td,
  .ant-table-tbody > tr > .ant-table-cell {
    padding: 3px 5px !important;
    &:first-child {
      padding-left: 10px !important;
    }
    &:last-child {
      padding-right: 10px !important;
    }
  }
  .ant-table-thead > tr > th {
    height: 30px !important;
  }
  .ant-table-tbody .ant-table-measure-row td {
    display: none;
  }
  /* sticky 헤더가 스크롤바 영역만큼 덜 차지하도록 해서 바디와 열 정렬 */
  .ant-table-sticky-holder {
    margin-right: var(--sbw, 0px);
    box-sizing: border-box;
  }
  /* sticky 시 헤더/바디 열 정렬 맞추기: 고정 너비 컬럼 강제 */
  .ant-table-thead > tr > th.dashboard-table-col-fix-width,
  .ant-table-tbody > tr > td.dashboard-table-col-fix-width {
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
    box-sizing: border-box;
  }
`;
